import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { StatusBadge } from "../components/product/StatusBadge";
import { formatPrice } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { AdminGuard, AdminNav } from "../components/admin/AdminShell";
import { Package, ShieldCheck, EnvelopeSimple, Certificate } from "@phosphor-icons/react";

const COMPLIANCE = ["Pending Review","Research Account Only","Institutional Account Only","Licensed Professional Only","Prescription Workflow Required","Not Available for Sale","Approved for Standard Checkout"];
const CHECKOUT = ["Request Availability","Apply for Account","Login to Purchase","Add to Cart","Prescription Required","Temporarily Unavailable","Out of Stock"];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [availabilityRequests, setAvailabilityRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [p, a, ap] = await Promise.all([
        api.get("/admin/products"),
        api.get("/admin/availability-requests"),
        api.get("/admin/account-applications"),
      ]);
      setProducts(p.data);
      setAvailabilityRequests(a.data);
      setApplications(ap.data);
    } catch (err) {
      // Non-admin roles hit product-only routes and 403 on the other two — expected, non-fatal.
      if (process.env.NODE_ENV !== "production" && err?.response?.status !== 403) {
        console.warn("AdminDashboard.load failed:", err);
      }
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const updateProduct = async (id, changes) => {
    try {
      const { data } = await api.patch(`/admin/products/${id}`, changes);
      setProducts((prev) => prev.map((p) => (p.id === id ? data : p)));
      toast.success("Updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const updateApp = async (id, changes) => {
    try {
      const { data } = await api.patch(`/admin/account-applications/${id}`, changes);
      setApplications((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success("Application updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const clearSelection = () => setSelectedIds([]);
  const selectAllVisible = (ids) => setSelectedIds(ids);

  const bulkApply = async (changes, label) => {
    if (selectedIds.length === 0) { toast.error("Select at least one product"); return; }
    try {
      await api.post("/admin/products/bulk", { product_ids: selectedIds, changes });
      toast.success(`${label} applied to ${selectedIds.length} product(s)`);
      await load();
      clearSelection();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-24 text-brand-muted">Loading…</div>;

  const filtered = products.filter((p) => !filter || p.product_name.toLowerCase().includes(filter.toLowerCase()) || p.public_sku.toLowerCase().includes(filter.toLowerCase()));

  return (
    <AdminGuard>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 py-10" data-testid="admin-dashboard">
      <div className="overline mb-3">Administrator</div>
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-brand-navy">Command deck</h1>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-px bg-brand-surface border border-brand-border">
        {[
          [Package, "Products", products.length],
          [EnvelopeSimple, "Availability requests", availabilityRequests.length],
          [ShieldCheck, "Applications", applications.length],
          [Certificate, "Published COAs", products.filter((p) => p.coa_status === "Published").length],
        ].map(([Icon, k, v]) => (
          <div key={k} className="bg-white p-6">
            <div className="overline mb-2 flex items-center gap-2"><Icon size={14} weight="duotone" className="text-brand-accent" /> {k}</div>
            <div className="font-mono text-2xl text-brand-navy">{v}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products" className="mt-10">
        <TabsList className="bg-transparent p-0 border-b border-brand-border rounded-none w-full justify-start h-auto flex-wrap">
          {[["products","Products"], ["availability","Availability Requests"], ["applications","Account Applications"]].map(([k,l]) => (
            <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-navy data-[state=active]:text-brand-accent data-[state=active]:bg-transparent text-brand-muted hover:text-brand-text px-4 py-3 text-sm font-medium">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="products" className="pt-6">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              placeholder="Filter by name or SKU"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="admin-product-filter"
              className="w-full max-w-sm bg-white/60 border border-brand-border px-3 py-2 text-sm text-brand-navy focus:border-cyan-500 outline-none"
            />
            <button
              type="button"
              onClick={() => selectAllVisible(filtered.map((p) => p.id))}
              className="text-[11px] text-brand-muted hover:text-brand-accent font-mono uppercase tracking-widest"
              data-testid="admin-select-all"
            >Select all visible</button>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={clearSelection}
                className="text-[11px] text-brand-muted hover:text-red-300 font-mono uppercase tracking-widest"
                data-testid="admin-clear-selection"
              >Clear ({selectedIds.length})</button>
            )}
          </div>

          {selectedIds.length > 0 && (
            <div className="mb-4 border border-brand-navy bg-brand-accent/10 p-4 flex flex-wrap items-center gap-3" data-testid="admin-bulk-bar">
              <div className="text-xs text-brand-accent font-mono uppercase tracking-widest">Bulk actions · {selectedIds.length} selected</div>
              <button
                onClick={() => bulkApply({ ecommerce_enabled: true, checkout_status: "Add to Cart", card_payment_allowed: true, compliance_status: "Approved for Standard Checkout" }, "Enable ecommerce")}
                className="text-xs bg-brand-navy text-white hover:bg-brand-navy2 font-semibold px-3 py-1.5"
                data-testid="admin-bulk-enable-ecommerce"
              >Enable ecommerce</button>
              <button
                onClick={() => bulkApply({ ecommerce_enabled: false, checkout_status: "Request Availability", card_payment_allowed: false }, "Disable ecommerce")}
                className="text-xs border border-slate-600 text-brand-text hover:bg-brand-surface px-3 py-1.5"
                data-testid="admin-bulk-disable-ecommerce"
              >Disable ecommerce</button>
              <button
                onClick={() => bulkApply({ featured: true }, "Feature")}
                className="text-xs border border-slate-600 text-brand-text hover:bg-brand-surface px-3 py-1.5"
                data-testid="admin-bulk-feature"
              >Mark featured</button>
            </div>
          )}

          <div className="border border-brand-border divide-y divide-brand-border">
            {filtered.slice(0, 200).map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-3 items-center px-6 py-4 bg-white text-sm">
                <div className="col-span-3 flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    data-testid={`admin-select-${p.slug}`}
                    className="mt-1 accent-cyan-400"
                  />
                  <div>
                    <div className="font-display text-brand-navy">{p.product_name}</div>
                    <div className="text-xs text-brand-muted font-mono">{p.public_sku} · {p.strength}</div>
                  </div>
                </div>
                <div className="col-span-1 font-mono text-brand-accent text-xs">{formatPrice(p.retail_price_cents)}</div>
                <div className="col-span-2">
                  <Select value={p.compliance_status} onValueChange={(v) => updateProduct(p.id, { compliance_status: v })}>
                    <SelectTrigger data-testid={`admin-compliance-${p.slug}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COMPLIANCE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Select value={p.checkout_status} onValueChange={(v) => updateProduct(p.id, { checkout_status: v, card_payment_allowed: v === "Add to Cart" })}>
                    <SelectTrigger data-testid={`admin-checkout-${p.slug}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CHECKOUT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <input type="number" min={0} defaultValue={p.inventory_quantity} onBlur={(e) => updateProduct(p.id, { inventory_quantity: parseInt(e.target.value || 0, 10) })} data-testid={`admin-inv-${p.slug}`} className="w-full bg-brand-surface border border-brand-border px-2 py-1.5 text-xs font-mono text-brand-navy" />
                </div>
                <div className="col-span-2 flex flex-col gap-1">
                  <label className="inline-flex items-center gap-2 text-[11px] text-brand-text cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!p.ecommerce_enabled}
                      onChange={(e) => updateProduct(p.id, {
                        ecommerce_enabled: e.target.checked,
                        ...(e.target.checked ? { checkout_status: "Add to Cart", card_payment_allowed: true } : {}),
                      })}
                      data-testid={`admin-ecomm-${p.slug}`}
                      className="accent-cyan-400"
                    />
                    Ecommerce
                  </label>
                  <label className="inline-flex items-center gap-2 text-[11px] text-brand-muted cursor-pointer">
                    <input type="checkbox" checked={!!p.featured} onChange={(e) => updateProduct(p.id, { featured: e.target.checked })} data-testid={`admin-featured-${p.slug}`} className="accent-cyan-400" />
                    Featured
                  </label>
                </div>
                <div className="col-span-1 text-right">
                  <div className={`inline-block px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest ${p.ecommerce_enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-brand-surface text-brand-muted"}`}>
                    {p.ecommerce_enabled ? "Live" : "Off"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="availability" className="pt-6">
          {availabilityRequests.length === 0 ? (
            <div className="border border-brand-border p-10 bg-white text-sm text-brand-muted">No availability requests yet.</div>
          ) : (
            <div className="border border-brand-border divide-y divide-brand-border">
              {availabilityRequests.map((r) => (
                <div key={r.id} className="p-6 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-brand-navy">{r.product_name} <span className="text-brand-muted font-mono text-xs">· {r.strength}</span></div>
                      <div className="text-xs text-brand-muted">{r.name} · {r.email} {r.organization ? `· ${r.organization}` : ""}</div>
                    </div>
                    <StatusBadge status={r.status} kind="compliance" />
                  </div>
                  <p className="mt-3 text-sm text-brand-text">{r.intended_use}</p>
                  {r.message && <p className="mt-2 text-xs text-brand-muted">{r.message}</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications" className="pt-6">
          {applications.length === 0 ? (
            <div className="border border-brand-border p-10 bg-white text-sm text-brand-muted">No account applications yet.</div>
          ) : (
            <div className="space-y-4">
              {applications.map((a) => (
                <div key={a.id} className="border border-brand-border bg-white p-6">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="font-display text-brand-navy">{a.business_name}</div>
                      <div className="text-xs text-brand-muted font-mono uppercase tracking-wider">{a.account_type.replace(/_/g," ")} · {a.legal_name} · {a.business_email}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select value={a.status} onValueChange={(v) => updateApp(a.id, { status: v })}>
                        <SelectTrigger className="h-9 w-52 text-xs" data-testid={`admin-app-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["pending_review","needs_more_info","approved","declined"].map((s) => (
                            <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-brand-text">{a.intended_product_use}</p>
                  <Textarea placeholder="Reviewer notes…" defaultValue={a.reviewer_notes || ""} onBlur={(e) => updateApp(a.id, { reviewer_notes: e.target.value })} className="mt-3 text-xs" data-testid={`admin-app-notes-${a.id}`} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      </div>
    </AdminGuard>
  );
}
